import { PropertyDetails } from '.'

const scotlandIntervals = {
  'Single dwelling': [
    {
      from: 0,
      to: 145000,
      rate: 0,
    },
    {
      from: 145000,
      to: 250000,
      rate: 2,
    },
    {
      from: 250000,
      to: 325000,
      rate: 5,
    },
    {
      from: 325000,
      to: 750000,
      rate: 10,
    },
    {
      from: 750000,
      to: undefined,
      rate: 12,
    },
  ],
  'First-time buyer': [
    {
      from: 0,
      to: 175000,
      rate: 0,
    },
    {
      from: 175000,
      to: 250000,
      rate: 2,
    },
    {
      from: 250000,
      to: 325000,
      rate: 5,
    },
    {
      from: 325000,
      to: 750000,
      rate: 10,
    },
    {
      from: 750000,
      to: undefined,
      rate: 12,
    },
  ],
  'Additional property': [
    {
      from: 0,
      to: 145000,
      rate: 6,
    },
    {
      from: 145000,
      to: 250000,
      rate: 8,
    },
    {
      from: 250000,
      to: 325000,
      rate: 11,
    },
    {
      from: 325000,
      to: 750000,
      rate: 16,
    },
    {
      from: 750000,
      to: undefined,
      rate: 18,
    },
  ],
}

const walesIntervals = {
  'First-time buyer': [
    {
      from: 0,
      to: 225000,
      rate: 0,
    },
    {
      from: 225000,
      to: 400000,
      rate: 6,
    },
    {
      from: 400000,
      to: 750000,
      rate: 7.5,
    },
    {
      from: 750000,
      to: 1500000,
      rate: 10,
    },
    {
      from: 1500000,
      to: undefined,
      rate: 12,
    },
  ],
  'Additional property': [
    {
      from: 0,
      to: 180000,
      rate: 4,
    },
    {
      from: 180000,
      to: 250000,
      rate: 7.5,
    },
    {
      from: 250000,
      to: 400000,
      rate: 9,
    },
    {
      from: 400000,
      to: 750000,
      rate: 11.5,
    },
    {
      from: 750000,
      to: 1500000,
      rate: 14,
    },
    {
      from: 1500000,
      to: undefined,
      rate: 16,
    },
  ],
}

const englandAndIrelandIntervals = {
  'First-time buyer': {
    'Below 625k': [
      {
        from: 0,
        to: 425000,
        rate: 0,
      },
      {
        from: 425000,
        to: 925000,
        rate: 5,
      },
      {
        from: 925000,
        to: 1500000,
        rate: 10,
      },
      {
        from: 1500000,
        to: undefined,
        rate: 12,
      },
    ],
    'Above 625k': [
      {
        from: 0,
        to: 250000,
        rate: 0,
      },
      {
        from: 250000,
        to: 925000,
        rate: 5,
      },
      {
        from: 925000,
        to: 1500000,
        rate: 10,
      },
      {
        from: 1500000,
        to: undefined,
        rate: 12,
      },
    ],
  },
  'Moving home': [
    {
      from: 0,
      to: 250000,
      rate: 4,
    },
    {
      from: 250000,
      to: 925000,
      rate: 5,
    },
    {
      from: 925000,
      to: 1500000,
      rate: 10,
    },
    {
      from: 1500000,
      to: undefined,
      rate: 12,
    },
  ],
  'Additional property': [
    {
      from: 0,
      to: 250000,
      rate: 3,
    },
    {
      from: 250000,
      to: 925000,
      rate: 8,
    },
    {
      from: 925000,
      to: 1500000,
      rate: 13,
    },
    {
      from: 1500000,
      to: undefined,
      rate: 15,
    },
  ],
}

export type TaxInterval = {
  taxableRange: number
  taxPaid: number
}

export const calculateStampDuty = (propertyDetails: PropertyDetails) => {
  const { value, type, region } = propertyDetails
  let intervals
  switch (region) {
    case 'Scotland':
      if (type === 'Moving home') {
        intervals = scotlandIntervals['Additional property']
      } else {
        intervals = scotlandIntervals[type]
      }
      break
    case 'Wales':
      if (type === 'Moving home') {
        intervals = walesIntervals['Additional property']
      } else {
        intervals = walesIntervals[type]
      }
      break
    case 'England or Northern Ireland':
      if (type === 'First-time buyer') {
        intervals =
          value < 625000
            ? englandAndIrelandIntervals[type]['Below 625k']
            : englandAndIrelandIntervals[type]['Above 625k']
      } else {
        intervals = englandAndIrelandIntervals[type]
      }
      break
    default:
      intervals = []
  }

  let remainder = value
  let stampDuty = 0
  const stampDutyPerInterval: TaxInterval[] = []
  for (let i = 0; i < intervals?.length; i++) {
    if (value <= 0) break
    const { from: valueRangeFrom, to: valueRangeTo, rate } = intervals[i]
    const rangeRate = rate / 100
    if (remainder > 0) {
      let rangeStart, rangeEnd, taxableRange, taxPaid
      if (valueRangeFrom === 0) {
        rangeStart = 0
        rangeEnd = valueRangeTo
        taxableRange = Math.min(rangeEnd, remainder)
        taxPaid = Math.round(taxableRange * rangeRate)
      } else if (valueRangeTo) {
        rangeStart = valueRangeFrom
        rangeEnd = valueRangeTo
        taxableRange = Math.min(rangeEnd - rangeStart, remainder)
        taxPaid = Math.round(taxableRange * rangeRate)
      } else {
        taxableRange = remainder
        taxPaid = Math.round(taxableRange * rangeRate)
      }
      remainder -= taxableRange
      stampDuty += taxPaid
      stampDutyPerInterval.push({ taxableRange: taxableRange, taxPaid: taxPaid })
    }
  }

  return { stampDuty: stampDuty, intervals: intervals, stampDutyPerInterval: stampDutyPerInterval }
}

export default calculateStampDuty
